import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { useOutletContext } from "react-router-dom";
import QuestionMarkRoundedIcon from "@mui/icons-material/QuestionMarkRounded";
import CloseIcon from "@mui/icons-material/Close";
import {
  Stack,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from "@mui/material";

import EcrfBlocks from "../Trials/EcrfBlocks";
import UpdateStatusPopUp from "../PopUp/UpdateStatusPopUpPopUp";
import GenerateQueryPopUp from "../PopUp/GenerateQueryPopUp";
import QueryTableContainer from "./QueryTableContainer";
import ChatRoom from "./ChatRoom";
import useGetTrialForPatient from "../../hooks/useGetTrialForPatient";
import useGetQueryLogs from "../../hooks/useGetQueryLogs";
import useGetVisitsDropdownStatus from "../../hooks/useGetVisitsDropdownStatus";
import Loader from "../Loader";

const QueryTable = () => {
  const { _id_ } = useParams();
  const user_data = JSON.parse(localStorage.getItem("user_data"));
  const role = user_data.role;

  const { infoData, trialFilter, setActive, setBreadcrumbTitle, isTrialLocked } =
    useOutletContext() ?? {};
  setActive("query");
  setBreadcrumbTitle("Query");

  const [queryLogData, setQueryLogData] = useState([]);
  const [trialId, setTrialId] = useState(0);
  const [checked, setChecked] = useState(Array(0).fill(false));
  const [showModal, setShowModal] = useState(false);
  const [inputType, setInputType] = useState(undefined);
  const [showRolesModal, setShowRolesModal] = useState(false);
  const [showChatModal, setShowChatModal] = useState(false);
  const [showQueryTableModal, setShowQueryTableModal] = useState(false);
  const [blocksSelected, setBlocksSelected] = useState([]);
  const [hyperLinkObject, setHyperLinkObject] = useState({});
  const [generateQueryObject, setGenerateQueryObject] = useState({});
  const [generateQueryMessageObject, setGenerateQueryMessageObject] = useState({});
  const [showEcrfBlock, setShowEcrfBlock] = useState(false);
  const [searchObject, setSearchObject] = useState({
    visit: null,
    block_name: "",
    inv_certified: "All",
    cro_block_status: "All",
    dm_block_status: "All",
    block_status: "All",
  });

  const urlSearchString = window.location.search;
  const params = new URLSearchParams(urlSearchString);

  const convertFirstLetterToCapital = (str) => {
    const newStr = str.charAt(0).toUpperCase() + str.slice(1);
    return newStr;
  };

  const handleParentChange = (event) => {
    const filteredQueryLog = queryLogData.filter((item) => item.status !== "Not Started");
    setChecked(Array(queryLogData.length).fill(event.target.checked));
    if (event.target.checked) {
      setBlocksSelected((prev) => filteredQueryLog.map((i) => i.id));
    } else {
      setBlocksSelected([]);
    }
  };

  const handleChildChange = (event, index, answerJsonId) => {
    const newChecked = [...checked];
    newChecked[index] = event.target.checked;
    setChecked(newChecked);
    setBlocksSelected((prev) => {
      const filtered = prev.filter((i) => i !== answerJsonId);
      if (event.target.checked) {
        return [...filtered, answerJsonId];
      } else {
        return filtered;
      }
    });
  };

  useEffect(() => {
    if (queryLogData.length > 0) {
      setChecked(Array(queryLogData.length).fill(false));

      if (params.get("queryId")) {
        const filterdItem = queryLogData.filter(
          (data) => data.query_status?.id === parseInt(params.get("queryId"))
        );
        if (filterdItem.length > 0) {
          const item = filterdItem[0];
          setGenerateQueryMessageObject({
            queryId: item.query_status?.id,
            queryStatus: item.query_status?.status,
            fromUserId: item.query_status?.from_user,
          });
          setShowChatModal(true);
        }
      }
    }
    setBlocksSelected([]);
  }, [queryLogData]);

  const [allChecked, setAllChecked] = useState(false);
  const [someChecked, setSomeChecked] = useState(false);

  useEffect(() => {
    setAllChecked(checked.every((value) => value === true));
    setSomeChecked(checked.some((value) => value === true));
  }, [checked]);

  const { data: trialData, isLoading: isPatientTrialsLoading } = useGetTrialForPatient({
    patientId: _id_,
    trialId: trialFilter,
    enabled: !!trialFilter,
  });

  useEffect(() => {
    if (trialData) {
      setTrialId(trialData?.trial?.id);
    }
  }, [trialData]);

  const { data: downdownVisitsStatus, isLoading: isVisitsDropdownLoading } =
    useGetVisitsDropdownStatus({
      trialId: trialId,
      patientId: _id_,
      enabled: !!_id_ && !!trialId,
    });

  const { data: queryLog, isFetching: isQueriesFetching } = useGetQueryLogs({
    patientId: _id_,
    trialId: trialId,
    searchParams: searchObject,
    enabled: trialId > 0 && Boolean(searchObject?.visit),
  });

  useEffect(() => {
    if (downdownVisitsStatus && downdownVisitsStatus.length > 0) {
      const visit =
        downdownVisitsStatus.find((v) => v.visit_number === -3) ||
        downdownVisitsStatus.find((v) => v.visit_number === 0);

      if (!visit) return;

      setSearchObject((prev) => {
        if (prev.visit === visit.visit_name) return prev;
        return { ...prev, visit: visit.visit_name };
      });
    }
  }, [downdownVisitsStatus]);

  useEffect(() => {
    if (queryLog) {
      setQueryLogData(queryLog);
    }
  }, [queryLog]);

  const handleHyperLink = (item) => {
    setHyperLinkObject({
      visit_number: item.visit_number,
      selectedBlock: item.block_name,
      visit: item.visit,
      patient_id: _id_,
      photo_session: true,
      isOpen: item.status !== "Not Started",
    });
  };

  const handleGenerateQuery = (item) => {
    setGenerateQueryObject({
      answerJsonId: item.id,
    });
  };

  const handleGenerateQueryMessage = (item) => {
    setGenerateQueryMessageObject({
      queryId: item.query_status?.id,
      queryStatus: item.query_status?.status,
      fromUserId: item.query_status?.from_user,
    });
  };

  useEffect(() => {
    if (hyperLinkObject.selectedBlock) {
      setShowEcrfBlock(true);
    } else {
      setShowEcrfBlock(false);
    }
  }, [hyperLinkObject]);

  const queryTableContainerProps = {
    allChecked,
    someChecked,
    handleParentChange,
    searchObject,
    queryLogData,
    checked,
    blocksSelected,
    handleChildChange,
    convertFirstLetterToCapital,
    handleHyperLink,
    handleGenerateQueryMessage,
    setShowChatModal,
    handleGenerateQuery,
    setShowRolesModal,
    QuestionMarkRoundedIcon,
    downdownVisitsStatus,
    setSearchObject,
    setShowModal,
    role,
    isTrialLocked,
    setInputType,
    showQueryTableModal,
    isLoading: isQueriesFetching,
  };

  return (
    <Stack sx={{ p: 2 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        width="100%"
        sx={{ mb: 2 }}
      >
        <Typography variant="h6">Query Table</Typography>

        <Button variant="contained" size="small" onClick={() => setShowQueryTableModal(true)}>
          Full Screen View
        </Button>
      </Stack>

      {isPatientTrialsLoading || isVisitsDropdownLoading ? (
        <Loader />
      ) : (
        <>
          {/* Fullscreen dialog */}
          <Dialog
            open={showQueryTableModal}
            onClose={() => setShowQueryTableModal(false)}
            fullScreen
          >
            <DialogTitle>
              Query Table
              <IconButton
                onClick={() => setShowQueryTableModal(false)}
                sx={{ position: "absolute", right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <QueryTableContainer {...queryTableContainerProps} />
            </DialogContent>
          </Dialog>

          {/* Inline table */}
          <QueryTableContainer {...queryTableContainerProps} />

          <UpdateStatusPopUp
            showModal={showModal}
            setShowModal={setShowModal}
            blocksSelected={blocksSelected}
            inputType={inputType}
            queryLogData={queryLogData}
            infoData={infoData}
            visit_number={hyperLinkObject.visit_number}
            trialId={trialId}
            patient_id={_id_}
          />
          <GenerateQueryPopUp
            showRolesModal={showRolesModal}
            setShowRolesModal={setShowRolesModal}
            answerJsonId={generateQueryObject?.answerJsonId}
            trialId={trialId}
          />
          {showChatModal && (
            <ChatRoom
              showChatModal={showChatModal}
              setShowChatModal={setShowChatModal}
              generateQueryMessageObject={generateQueryMessageObject}
              setGenerateQueryMessageObject={setGenerateQueryMessageObject}
            />
          )}
          {showEcrfBlock && (
            <EcrfBlocks
              visit_number={hyperLinkObject?.visit_number}
              visitName={searchObject?.visit}
              trialId={trialId}
              trialName={trialData?.trial?.name}
              selectedBlock={hyperLinkObject.selectedBlock}
              fromQueryTable={true}
              showEcrfBlock={showEcrfBlock}
              setShowEcrfBlock={setShowEcrfBlock}
              isOpen={hyperLinkObject.isOpen}
              patientId={_id_}
              isTrialLocked={isTrialLocked}
              selectedSessionId={
                hyperLinkObject?.visit_number === -2 || hyperLinkObject?.visit_number === -1
                  ? hyperLinkObject.visit
                  : 0
              }
            />
          )}
        </>
      )}
    </Stack>
  );
};

export default QueryTable;
